import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
  Alert,
  Checkbox,
  FormControl,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Typography,
  Box
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Button, Col, Container, Form, Row } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import { useNotification } from '../../../hooks/NotificationContext';
import {
  getProjectConfig,
  setProjectConfig,
} from '../../../modules/executions';
import {
  browserOptions,
  cloudDeviceLabs,
  platforms,
  selectStyle,
  subPlatformOptions,
} from '../../../static-data/configDropdownOptions';
import Loader from '../../elements/Loader';
import MultiSelect from '../../elements/MultiSelect';
import CountrySelect from './Countries';
import LanguageSelect from './Languages';
import useExecutionScope from '../../../hooks/useExecutionScope';
import ConfirmationModal from '../../elements/ConfirmationModal';

const defaultFormData = {
  geographies: [],
  languages: [],
  cloud_device: [],
  platforms: [],
  sub_platforms: [],
  browsers: [],
  other_cloud_device: '',
  other_browsers: '',
  multiple_geographies: 'No',
  multiple_languages: 'No',
};

const ExecutionScope = ({ isAdmin }) => {
  const { projectId } = useParams();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formState, setFormState] = useState(defaultFormData);
  const [isEditing, setIsEditing] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const { addNotification } = useNotification();
  const { isLoading, isPlatformsDefined } = useExecutionScope(projectId);

  const setFormData = (config) => {
    setFormState({
      geographies: config?.geographies || [],
      languages: config?.languages || [],
      cloud_device: config?.cloud_device || [],
      platforms: config?.platforms || [],
      sub_platforms: config?.sub_platforms || [],
      browsers: config?.browsers || [],
      other_cloud_device: config?.other_cloud_device || '',
      other_browsers: Array.isArray(config?.other_browsers)
        ? config.other_browsers.join(', ')
        : config?.other_browsers || '',
      multiple_geographies: config?.multiple_geographies || 'No',
      multiple_languages: config?.multiple_languages || 'No',
    });
  };

  const fetchProjectData = async () => {
    setLoading(true);
    try {
      const response = await getProjectConfig(projectId);
      setFormData(response);
    } catch (error) {
      console.error(error);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchProjectData();
  }, [projectId]);

  const handleChange = (name, value) => {
    if (name === 'platforms') {
      const filteredSubPlatforms = getFilteredSubPlatforms(value);

      setFormState((prevState) => ({
        ...prevState,
        platforms: value,
        sub_platforms:
          filteredSubPlatforms?.length > 0
            ? prevState.sub_platforms.filter((sub) =>
              filteredSubPlatforms.find((f) => f.name === sub)
            )
            : [],
      }));
    } else {
      setFormState((prevState) => ({
        ...prevState,
        [name]: value,
      }));
    }
  };
  const handleSaveClick = () => {
    setShowConfirmModal(true);
  };

  const handleSave = async () => {
    setShowConfirmModal(false);
    setSubmitting(true);
    try {
      await setProjectConfig(projectId, formState);
      setSubmitting(false);
      addNotification('success', 'Configurations updated successfully!');
      await fetchProjectData();
      setIsEditing(false);
    } catch (error) {
      addNotification('error', 'Failed to update configurations');
      setSubmitting(false);
    }
  };

  const getFilteredSubPlatforms = (selectedPlatforms) => {
    const isWebSelected = selectedPlatforms.includes('Web');
    const isMobileSelected =
      selectedPlatforms.includes('Mobile Responsive') ||
      selectedPlatforms.includes('Native Mobile App') ||
      selectedPlatforms.includes('Hybrid Mobile App');

    return subPlatformOptions.filter(
      (option) =>
        (option.category === 'Web' && isWebSelected) ||
        (option.category === 'Mobile' && isMobileSelected)
    );
  };

  const getFilteredSubPlatformOptions = () => {
    return getFilteredSubPlatforms(formState.platforms);
  };

  const showSubplatforms =
    formState.platforms.includes('Web') ||
    formState.platforms.some((platform) =>
      ['Mobile Responsive', 'Native Mobile App', 'Hybrid Mobile App'].includes(
        platform
      )
    );

  if (loading) return <Loader />;

  return (
    <Container fluid className="px-0">
      <ConfirmationModal
        show={showConfirmModal}
        handleClose={() => setShowConfirmModal(false)}
        handleConfirm={handleSave}
        title="Save Changes"
        body={
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
            <Box>
              <Typography variant="body2" sx={{ mb: 0.5 }}> Some configurations have been modified. </Typography>
              <Typography variant="body2" sx={{ mb: 1.5 }}> Removed values will be automatically removed from existing <strong> Test Suites </strong>, <strong> Test Cases </strong>, <strong> Test Cycles </strong>, and <strong> Iterations </strong> that currently use them. </Typography>
              <Typography variant="body2" color="warning.main"> This action cannot be undone. Please review your changes before saving. </Typography>
            </Box>
          </Box>}
        buttonLabel="Confirm & Save"
        buttonStyle="primary"
        loading={submitting}
      />
      {!isLoading && !isPlatformsDefined && formState?.platforms?.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No testing platforms have been selected. Specifying them will improve
          testing coverage and efficiency.
        </Alert>
      )}
      <Form>
        <Row className="mb-2">
          <Col md={6}>
            <Form.Group controlId="platforms">
              <Form.Label>Testing Platform</Form.Label>
              {isEditing ? (
                <MultiSelect
                  options={platforms}
                  selectedValues={formState.platforms}
                  onChange={(value) => handleChange('platforms', value)}
                  placeholder="Select platforms"
                />
              ) : (
                <p className="form-static-text">
                  {formState?.platforms?.length > 0
                    ? formState?.platforms?.join(', ')
                    : 'None'}
                </p>
              )}
            </Form.Group>
          </Col>

          {showSubplatforms && (
            <>
              <Col md={6}>
                <Form.Group controlId="subPlatformOptions">
                  <Form.Label className="required">
                    Supported Options
                  </Form.Label>
                  {isEditing ? (
                    <FormControl sx={{ width: '100%', mb: 2 }}>
                      <Select
                        multiple
                        sx={selectStyle}
                        IconComponent={KeyboardArrowDownIcon}
                        value={formState.sub_platforms || []}
                        onChange={(e) =>
                          handleChange('sub_platforms', e.target.value)
                        }
                        input={<OutlinedInput />}
                        renderValue={(selected) =>
                          selected?.length === 0 ? (
                            <span style={{ color: '#888' }}>
                              Select options
                            </span>
                          ) : (
                            selected.join(', ')
                          )
                        }
                        displayEmpty>
                        {['Web', 'Mobile'].map((category) => {
                          const categoryFilteredOptions =
                            getFilteredSubPlatformOptions().filter(
                              (option) => option.category === category
                            );

                          if (categoryFilteredOptions?.length > 0) {
                            return [
                              <MenuItem key={`${category}-label`} disabled>
                                <strong>{category}</strong>
                              </MenuItem>,
                              ...categoryFilteredOptions.map((option) => (
                                <MenuItem key={option.id} value={option.name}>
                                  <Checkbox
                                    disableRipple
                                    checked={formState.sub_platforms?.includes(
                                      option.name
                                    )}
                                  />
                                  <ListItemText
                                    primary={
                                      <span style={{ textTransform: 'none' }}>
                                        {option.name}
                                      </span>
                                    }
                                  />
                                </MenuItem>
                              )),
                            ];
                          }
                          return null;
                        })}
                      </Select>
                    </FormControl>
                  ) : (
                    <p className="form-static-text">
                      {formState?.sub_platforms?.length > 0
                        ? formState?.sub_platforms?.join(', ')
                        : 'None'}
                    </p>
                  )}
                </Form.Group>
              </Col>
            </>
          )}
        </Row>
        {showSubplatforms && (
          <Row className="mb-2">
            <Col md={6}>
              <Form.Group controlId="browsers">
                <Form.Label>Browsers</Form.Label>
                {isEditing ? (
                  <MultiSelect
                    options={browserOptions}
                    selectedValues={formState.browsers}
                    onChange={(value) => handleChange('browsers', value)}
                    placeholder="Select Browsers"
                  />
                ) : (
                  <p className="form-static-text">
                    {formState?.browsers?.length > 0
                      ? formState?.browsers
                        ?.filter((browser) => browser !== 'Others')
                        .join(', ')
                      : 'None'}
                    {formState?.browsers?.includes('Others') &&
                      formState?.other_browsers
                      ? `${formState?.browsers?.filter(b => b !== 'Others').length > 0 ? ', ' : ''}Others (${formState.other_browsers})`
                      : ''}
                  </p>
                )}
              </Form.Group>
            </Col>
            {isEditing && formState?.browsers.includes('Others') && (
              <Col md={6}>
                <Form.Label>Specify Other Browsers (comma-separated)</Form.Label>
                <OutlinedInput
                  value={formState.other_browsers}
                  onChange={(e) =>
                    handleChange('other_browsers', e.target.value)
                  }
                  fullWidth
                />
              </Col>
            )}
          </Row>
        )}
        <hr />
        <Row className="mb-2">
          <Col md={6}>
            <Form.Group controlId="cloudDeviceLab">
              <Form.Label>
                Do you want to support multiple languages?
              </Form.Label>
              {isEditing ? (
                <FormControl fullWidth>
                  <Select
                    value={formState.multiple_languages}
                    onChange={(e) =>
                      handleChange('multiple_languages', e.target.value)
                    }
                    IconComponent={KeyboardArrowDownIcon}
                    sx={selectStyle}>
                    <MenuItem value={'Yes'}>Yes</MenuItem>
                    <MenuItem value={'No'}>No</MenuItem>
                  </Select>
                </FormControl>
              ) : (
                <span>
                  <p className="form-static-text">
                    {formState?.multiple_languages === 'Yes'
                      ? `Yes, ${formState?.languages?.length > 0 &&
                      formState?.languages?.join(', ')
                      }`
                      : 'No'}
                  </p>
                </span>
              )}
            </Form.Group>
          </Col>
          <Col>
            {isEditing && formState.multiple_languages === 'Yes' && (
              <LanguageSelect
                selectedValues={formState.languages}
                setSelectedValues={(newValue) =>
                  setFormState({
                    ...formState,
                    languages: newValue,
                  })
                }
              />
            )}
          </Col>
        </Row>
        <hr />
        <Row className="mb-2">
          <Col md={6}>
            <Form.Group controlId="cloudDeviceLab">
              <Form.Label>
                Do you want to support multiple geographies?
              </Form.Label>
              {isEditing ? (
                <FormControl fullWidth>
                  <Select
                    value={formState.multiple_geographies}
                    onChange={(e) =>
                      handleChange('multiple_geographies', e.target.value)
                    }
                    IconComponent={KeyboardArrowDownIcon}
                    sx={selectStyle}>
                    <MenuItem value={'Yes'}>Yes</MenuItem>
                    <MenuItem value={'No'}>No</MenuItem>
                  </Select>
                </FormControl>
              ) : (
                <span>
                  <p className="form-static-text">
                    {formState?.multiple_geographies === 'Yes'
                      ? `Yes, ${formState?.geographies?.length > 0 &&
                      formState?.geographies?.join(', ')
                      }`
                      : 'No'}
                  </p>
                </span>
              )}
            </Form.Group>
          </Col>
          <Col md={6}>
            {isEditing && formState.multiple_geographies === 'Yes' && (
              <CountrySelect
                selectedValues={formState.geographies}
                setSelectedValues={(newValue) =>
                  setFormState({
                    ...formState,
                    geographies: newValue,
                  })
                }
              />
            )}
          </Col>
        </Row>
        <hr />
        <Row className="mb-2">
          <Col md={6}>
            <Form.Group controlId="geographies">
              <Form.Label>Cloud Device Labs</Form.Label>
              {isEditing ? (
                <MultiSelect
                  options={cloudDeviceLabs.map((lab) => lab)}
                  selectedValues={formState.cloud_device}
                  onChange={(value) => handleChange('cloud_device', value)}
                  placeholder="Select Cloud Labs"
                />
              ) : (
                <p className="form-static-text">
                  {formState?.cloud_device?.length > 0
                    ? formState?.cloud_device
                      ?.filter((device) => device !== 'Others')
                      .join(', ')
                    : 'None'}
                  {formState?.cloud_device?.includes('Others') &&
                    formState?.other_cloud_device
                    ? `${formState?.cloud_device?.filter(d => d !== 'Others').length > 0 ? ', ' : ''}Others (${formState.other_cloud_device})`
                    : ''}
                </p>
              )}
            </Form.Group>
          </Col>
          {isEditing && formState?.cloud_device.includes('Others') && (
            <Col>
              <Form.Label>Specify the Other Cloud Device Lab</Form.Label>
              <OutlinedInput
                value={formState.other_cloud_device}
                onChange={(e) =>
                  handleChange('other_cloud_device', e.target.value)
                }
                fullWidth
              />
            </Col>
          )}
        </Row>

        <Row className="mt-2">
          <Col className="d-flex justify-content-start">
            {!isAdmin ? null : isEditing ? (
              <Button
                variant="primary"
                onClick={handleSaveClick}
                disabled={
                  submitting ||
                  (showSubplatforms && !formState?.sub_platforms?.length)
                }>
                {submitting ? 'Saving...' : 'Save Details'}
              </Button>
            ) : (
              <Button
                variant="primary"
                onClick={() => setIsEditing(true)}
                disabled={!isAdmin}>
                Edit Details
              </Button>
            )}
          </Col>
        </Row>
      </Form>
    </Container>
  );
};

export default ExecutionScope;
